/**
 * Consent Page - EULA and Privacy Policy acceptance + Clerk Sign-Up
 *
 * Version History:
 * v4.35 - 2026-10-29 - PILOT-GATE-BROWSER-ONLY-01 (row 737, Branko ruling 2026-10-28): Consent is the invited path only.
 *                      With no invite code it renders nothing and redirects to "/". With a code it shows only a spinner
 *                      until POST /api/invitations/validate answers; an invalid, used or revoked code (or a failed
 *                      check) redirects to "/". Every navigation to the non-existent "/waitlist" now goes to "/".
 * v4.34 - 2026-10-28 - WEBSITE-DISABLE-01 (row 785): temporarily disabled by Branko - the "Or sign up with" divider and the Continue with Google / Microsoft buttons (and their handlers) are removed from the Create Your Account page. Email + password sign-up, the invite-code path and the /sso-callback route are unchanged.
 * v3.95 - 2026-03-26 - ADMIN-003 Issue 3: redirect to /waitlist on invalid/expired/used invite token
 * v3.79 - 2026-03-14 - PILOT-02: inviteCode prop bypasses REGISTRATION_OPEN gate, validates code on mount, pre-fills email
 * v2.59 - 2026-02-04 - Redirect to login after registration (standard pattern for reliable session)
 * v2.55 - 2026-02-04 - Integrated Clerk sign-up with 3-phase flow (consent → signup → verification)
 * v2.49 - 2026-02-03 - Initial implementation with scroll-through and checkbox acceptance
 */
// v3.93 - Dark mode support: theme-aware bgcolor + transparent logo swap - 2026-03-19
import React, { useState, useRef, useEffect } from 'react';
import MyLojicLogo from '../components/MyLojicLogo';
import { useNavigate, Navigate, Link as RouterLink } from 'react-router-dom';
import { useSignUp, useAuth } from '@clerk/clerk-react';
import { useTheme } from '@mui/material/styles'; // v3.93
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab,
  Divider,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Link,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Visibility, VisibilityOff } from '@mui/icons-material'; // v4.34 - WEBSITE-DISABLE-01 (row 785): GoogleIcon import removed with the social buttons
import { colors } from '../theme/colors';
import { portalRequestUngated } from '../services/portalRequest'; // v4.28 - G10-B: one request wrapper, carries the chosen company

// v4.34 - WEBSITE-DISABLE-01 (row 785) - MicrosoftIcon removed with the social sign-up buttons (temporarily disabled by Branko 2026-10-28).

type ConsentPhase = 'consent' | 'signup' | 'verification';

// Embedded content components (simplified versions for the consent flow)
const EulaContent: React.FC = () => (
  <Box sx={{ p: 2 }}>
    <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
      END USER LICENSE AGREEMENT
    </Typography>
    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 3 }}>
      MyLojic Platform | Data Edge Services LLC | Effective: February 3, 2026
    </Typography>

    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'secondary.main' }}>
      IMPORTANT - PLEASE READ CAREFULLY
    </Typography>
    <Typography paragraph sx={{ fontSize: '0.9rem' }}>
      This End User License Agreement ("Agreement" or "EULA") is a legally binding contract between you and Data Edge Services LLC, doing business as MyLojic. By accessing or using the Platform, creating an account, or clicking "I Agree," you acknowledge that you have read, understood, and agree to be bound by this Agreement.
    </Typography>

    <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1, color: 'secondary.main' }}>
      1. License Grant
    </Typography>
    <Typography paragraph sx={{ fontSize: '0.9rem' }}>
      Subject to your compliance with this Agreement and payment of all applicable fees, the Company grants you a limited, non-exclusive, non-transferable, revocable license to access and use the Platform solely for your internal business purposes.
    </Typography>

    <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1, color: 'secondary.main' }}>
      2. Restrictions
    </Typography>
    <Typography paragraph sx={{ fontSize: '0.9rem' }}>
      You shall not copy, modify, reverse engineer, or create derivative works of the Platform. You may not sublicense, sell, or distribute the Platform to third parties.
    </Typography>

    <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1, color: 'secondary.main' }}>
      3. Your Business Data
    </Typography>
    <Typography paragraph sx={{ fontSize: '0.9rem' }}>
      You retain all rights to your Business Data. By submitting data to the Platform, you grant us a limited license to process it solely for providing our services. Your data is logically isolated from other users through our multi-tenant architecture.
    </Typography>

    <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1, color: 'secondary.main' }}>
      4. AI-Powered Features
    </Typography>
    <Typography paragraph sx={{ fontSize: '0.9rem' }}>
      The Platform uses AI technologies to analyze your data and provide insights. AI-generated recommendations are for informational purposes only and should not be considered professional advice. Material data transformations require your explicit approval.
    </Typography>

    <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1, color: 'secondary.main' }}>
      5. Payment and Subscription
    </Typography>
    <Typography paragraph sx={{ fontSize: '0.9rem' }}>
      Access is provided on a subscription basis. Setup fees are non-refundable. Monthly fees are non-refundable for the current billing cycle once processed.
    </Typography>

    <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1, color: 'secondary.main' }}>
      6. Disclaimer of Warranties
    </Typography>
    <Typography paragraph sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
      THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE." THE COMPANY DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE.
    </Typography>

    <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1, color: 'secondary.main' }}>
      7. Limitation of Liability
    </Typography>
    <Typography paragraph sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
      THE COMPANY'S LIABILITY SHALL NOT EXCEED THE FEES PAID BY YOU DURING THE TWELVE MONTHS PRECEDING THE CLAIM.
    </Typography>

    <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1, color: 'secondary.main' }}>
      8. Dispute Resolution
    </Typography>
    <Typography paragraph sx={{ fontSize: '0.9rem' }}>
      This Agreement is governed by Texas law. Disputes will be resolved through binding arbitration in Austin, Texas. You waive your right to participate in class action lawsuits.
    </Typography>

    <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1, color: 'secondary.main' }}>
      9. Termination
    </Typography>
    <Typography paragraph sx={{ fontSize: '0.9rem' }}>
      You may terminate at any time. Upon termination, your data will be deleted within 30 days unless retention is required by law.
    </Typography>

    <Divider sx={{ my: 3 }} />

    <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', textAlign: 'center' }}>
      This is a summary. Read the full EULA at <a href="/legal/eula" target="_blank" rel="noopener noreferrer">mylojic.com/legal/eula</a>
    </Typography>
  </Box>
);

const PrivacyContent: React.FC = () => (
  <Box sx={{ p: 2 }}>
    <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
      PRIVACY POLICY
    </Typography>
    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 3 }}>
      MyLojic Platform | Data Edge Services LLC | Effective: February 3, 2026
    </Typography>

    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'secondary.main' }}>
      1. Information We Collect
    </Typography>
    <Typography paragraph sx={{ fontSize: '0.9rem' }}>
      We collect account information (name, email, company), business data you upload or connect, and automatically collected data (device info, usage data, logs).
    </Typography>

    <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1, color: 'secondary.main' }}>
      2. How We Use Your Information
    </Typography>
    <Typography paragraph sx={{ fontSize: '0.9rem' }}>
      We use your information to provide and improve the Platform, process your business data through our analytics pipeline, generate AI-powered insights, communicate with you, and ensure security and compliance.
    </Typography>

    <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1, color: 'secondary.main' }}>
      3. Data Storage and Security
    </Typography>
    <Typography paragraph sx={{ fontSize: '0.9rem' }}>
      Your data is stored on Microsoft Azure infrastructure with AES-256 encryption at rest and TLS 1.2+ in transit. We implement multi-tenant isolation, Row-Level Security, and maintain 7-year audit trails.
    </Typography>

    <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1, color: 'secondary.main' }}>
      4. We Do Not Sell Your Data
    </Typography>
    <Typography paragraph sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
      We do not sell, rent, or trade your personal information or Business Data to third parties for marketing or advertising purposes.
    </Typography>

    <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1, color: 'secondary.main' }}>
      5. Service Providers
    </Typography>
    <Typography paragraph sx={{ fontSize: '0.9rem' }}>
      We share data with Microsoft Azure (infrastructure), Microsoft Azure OpenAI (AI analytics - your data is not used for training), and our payment processor (billing only).
    </Typography>

    <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1, color: 'secondary.main' }}>
      6. Your Rights
    </Typography>
    <Typography paragraph sx={{ fontSize: '0.9rem' }}>
      You have the right to access, correct, delete, and export your data. You can restrict processing and withdraw consent at any time. We will not discriminate against you for exercising these rights.
    </Typography>

    <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1, color: 'secondary.main' }}>
      7. Data Retention
    </Typography>
    <Typography paragraph sx={{ fontSize: '0.9rem' }}>
      We retain your data for the duration of your account plus 30 days. Audit logs are kept for 7 years. You can request earlier deletion at any time.
    </Typography>

    <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1, color: 'secondary.main' }}>
      8. International Transfers
    </Typography>
    <Typography paragraph sx={{ fontSize: '0.9rem' }}>
      Your data is stored in the United States. For EU/EEA transfers, we use Standard Contractual Clauses approved by the European Commission.
    </Typography>

    <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1, color: 'secondary.main' }}>
      9. Contact Us
    </Typography>
    <Typography paragraph sx={{ fontSize: '0.9rem' }}>
      Privacy requests: privacy@dataedgesvc.com
    </Typography>

    <Divider sx={{ my: 3 }} />

    <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', textAlign: 'center' }}>
      This is a summary. Read the full Privacy Policy at <a href="/legal/privacy" target="_blank" rel="noopener noreferrer">mylojic.com/legal/privacy</a>
    </Typography>
  </Box>
);

// v3.79 - PILOT-02: invite code prop
interface ConsentProps {
  inviteCode?: string;
}

const Consent: React.FC<ConsentProps> = ({ inviteCode }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const { isSignedIn, getToken } = useAuth();
  const muiTheme = useTheme(); // v3.93 - dark mode detection
  const isDark = muiTheme.palette.mode === 'dark';
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { signUp, setActive, isLoaded } = useSignUp();

  // Phase state
  const [phase, setPhase] = useState<ConsentPhase>('consent');

  // Consent state
  const [activeTab, setActiveTab] = useState(0);
  const [eulaScrolled, setEulaScrolled] = useState(false);
  const [privacyScrolled, setPrivacyScrolled] = useState(false);
  const [eulaAccepted, setEulaAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  // Sign-up form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Verification state
  const [verificationCode, setVerificationCode] = useState('');

  // Common state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // v3.79 - PILOT-02: invite code validation state
  const [inviteEmail, setInviteEmail] = useState<string>('');
  const [inviteValidating, setInviteValidating] = useState<boolean>(!!inviteCode);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const eulaRef = useRef<HTMLDivElement>(null);
  const privacyRef = useRef<HTMLDivElement>(null);

  // Redirect if already signed in
  React.useEffect(() => {
    if (isSignedIn) {
      navigate('/auth-landing', { replace: true });
    }
  }, [isSignedIn, navigate]);

  // v3.79 - PILOT-02: validate invite code on mount; pre-fill email from response
  useEffect(() => {
    if (!inviteCode) return;
    (async () => {
      setInviteValidating(true);
      try {
        const res = await portalRequestUngated('/api/invitations/validate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: inviteCode }),
        });
        const data = await res.json();
        if (data.valid) {
          setInviteEmail(data.email);
          setEmail(data.email); // pre-fill email field
        } else {
          setInviteError(data.reason || 'invalid');
        }
      } catch {
        setInviteError('network_error');
      } finally {
        setInviteValidating(false);
      }
    })();
  }, [inviteCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if user has scrolled to bottom of content
  const handleScroll = (e: React.UIEvent<HTMLDivElement>, type: 'eula' | 'privacy') => {
    const target = e.target as HTMLDivElement;
    const isAtBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 50;

    if (isAtBottom) {
      if (type === 'eula') {
        setEulaScrolled(true);
      } else {
        setPrivacyScrolled(true);
      }
    }
  };

  // Handle continue to registration (after consent)
  const handleContinueToSignup = () => {
    sessionStorage.setItem('consentTimestamp', new Date().toISOString());
    sessionStorage.setItem('eulaVersion', '1.0');
    sessionStorage.setItem('privacyVersion', '1.0');
    setPhase('signup');
    setError(null);
  };

  // Handle email/password sign-up
  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signUp) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await signUp.create({
        firstName,
        lastName,
        emailAddress: email,
        password,
      });

      if (result.status === 'missing_requirements') {
        // Need email verification
        await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
        setPhase('verification');
      } else if (result.status === 'complete') {
        // v3.95 - ADMIN-003 Issue 4: blocking accept + email ownership check; redirect to "/" on failure (v4.35)
        if (inviteCode && inviteEmail) {
          try {
            if (!result.createdSessionId) {
              navigate('/'); // v4.35 - PILOT-GATE-BROWSER-ONLY-01: was the non-existent /waitlist
              return;
            }
            await setActive({ session: result.createdSessionId });
            const token = await getToken();
            if (!token) {
              navigate('/'); // v4.35 - PILOT-GATE-BROWSER-ONLY-01: was the non-existent /waitlist
              return;
            }
            const acceptRes = await portalRequestUngated('/api/invitations/accept', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({ code: inviteCode }),
            });
            if (!acceptRes.ok) {
              navigate('/'); // v4.35 - PILOT-GATE-BROWSER-ONLY-01: was the non-existent /waitlist
              return;
            }
          } catch {
            navigate('/'); // v4.35 - PILOT-GATE-BROWSER-ONLY-01: was the non-existent /waitlist
            return;
          }
        }
        // v2.59 - Redirect to login after registration for reliable session establishment
        navigate('/login?registered=true');
      }
    } catch (err: any) {
      console.error('Sign-up error:', err);
      const errorCode = err.errors?.[0]?.code;
      switch (errorCode) {
        case 'form_identifier_exists':
          setError(t('signup.errors.emailInUse'));
          break;
        case 'form_password_pwned':
        case 'form_password_length_too_short':
          setError(t('signup.errors.weakPassword'));
          break;
        case 'form_param_format_invalid':
          setError(t('signup.errors.invalidEmail'));
          break;
        default:
          setError(t('signup.errors.generic'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle email verification
  const handleVerifyEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signUp) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await signUp.attemptEmailAddressVerification({
        code: verificationCode,
      });

      if (result.status === 'complete') {
        // v3.95 - ADMIN-003 Issue 4: blocking accept + email ownership check; redirect to "/" on failure (v4.35)
        if (inviteCode && inviteEmail) {
          try {
            if (!result.createdSessionId) {
              navigate('/'); // v4.35 - PILOT-GATE-BROWSER-ONLY-01: was the non-existent /waitlist
              return;
            }
            await setActive({ session: result.createdSessionId });
            const token = await getToken();
            if (!token) {
              navigate('/'); // v4.35 - PILOT-GATE-BROWSER-ONLY-01: was the non-existent /waitlist
              return;
            }
            const acceptRes = await portalRequestUngated('/api/invitations/accept', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({ code: inviteCode }),
            });
            if (!acceptRes.ok) {
              navigate('/'); // v4.35 - PILOT-GATE-BROWSER-ONLY-01: was the non-existent /waitlist
              return;
            }
          } catch {
            navigate('/'); // v4.35 - PILOT-GATE-BROWSER-ONLY-01: was the non-existent /waitlist
            return;
          }
        }
        // v2.59 - Redirect to login after email verification for reliable session
        navigate('/login?verified=true');
      }
    } catch (err: any) {
      console.error('Verification error:', err);
      const errorCode = err.errors?.[0]?.code;
      if (errorCode === 'form_code_incorrect') {
        setError(t('verification.errors.invalidCode'));
      } else if (errorCode === 'verification_expired') {
        setError(t('verification.errors.expiredCode'));
      } else {
        setError(t('verification.errors.generic'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Resend verification code
  const handleResendCode = async () => {
    if (!isLoaded || !signUp) return;

    setIsLoading(true);
    try {
      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      setError(null);
      // Could show a success toast here
    } catch (err) {
      console.error('Resend error:', err);
      setError(t('verification.errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  // v4.34 - WEBSITE-DISABLE-01 (row 785) - Google / Microsoft sign-up handlers removed with their buttons (temporarily disabled by Branko 2026-10-28).

  const canContinue = eulaAccepted && privacyAccepted;

  // v4.35 - PILOT-GATE-BROWSER-ONLY-01 (row 737): no public account creation. Without an invite code this page
  // never renders its consent or sign-up form; /register shows the request-early-access page instead.
  if (!inviteCode) {
    return <Navigate to="/" replace />;
  }

  // Show loading while Clerk initializes
  if (!isLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
        <CircularProgress sx={{ color: colors.primary.main }} />
      </Box>
    );
  }

  // v3.79 - PILOT-02: show spinner while validating invite code
  if (inviteValidating) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
        <CircularProgress sx={{ color: colors.primary.main }} />
      </Box>
    );
  }

  // v3.95 - ADMIN-003 Issue 3: redirect on invalid/expired/used invite token
  // v4.35 - PILOT-GATE-BROWSER-ONLY-01: to "/" (the former target /waitlist is not a route)
  if (inviteCode && inviteError) {
    return <Navigate to="/" replace />;
  }

  // PHASE 3: Email Verification
  if (phase === 'verification') {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Header */}
        <Box sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider', py: 2 }}>
          <Container maxWidth="md">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => setPhase('signup')}
                sx={{ color: 'text.secondary' }}
              >
                Back
              </Button>
              <MyLojicLogo isDark={isDark} height={36} />
            </Box>
          </Container>
        </Box>

        <Container maxWidth="sm" sx={{ py: 6 }}>
          <Paper elevation={3} sx={{ p: { xs: 3, sm: 6 }, borderRadius: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, textAlign: 'center', mb: 0.5, color: colors.text.primary }}>
              {t('verification.title')}
            </Typography>
            <Typography sx={{ textAlign: 'center', mb: 4, color: colors.text.secondary }}>
              {t('verification.subtitle', { email })}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3, bgcolor: colors.status.errorLight }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleVerifyEmail}>
              <TextField
                fullWidth
                label={t('verification.codeLabel')}
                placeholder={t('verification.codePlaceholder')}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                disabled={isLoading}
                required
                autoFocus
                sx={{ mb: 3 }}
                inputProps={{ maxLength: 6 }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={isLoading || verificationCode.length < 6}
                sx={{
                  bgcolor: colors.accent.main,
                  color: colors.accent.contrastText,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': { bgcolor: colors.accent.dark },
                  '&:disabled': { bgcolor: colors.border.main },
                }}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} sx={{ color: 'inherit', mr: 1 }} />
                    {t('verification.verifying')}
                  </>
                ) : (
                  t('verification.submitButton')
                )}
              </Button>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                {t('verification.resendCode')}{' '}
                <Link
                  component="button"
                  type="button"
                  onClick={handleResendCode}
                  disabled={isLoading}
                  sx={{
                    color: colors.primary.main,
                    fontWeight: 600,
                    textDecoration: 'none',
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                    '&:disabled': { color: colors.text.disabled, cursor: 'not-allowed' },
                  }}
                >
                  {t('verification.resendButton')}
                </Link>
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  // PHASE 2: Sign-Up Form
  if (phase === 'signup') {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Header */}
        <Box sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider', py: 2 }}>
          <Container maxWidth="md">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => setPhase('consent')}
                sx={{ color: 'text.secondary' }}
              >
                {t('signup.backToTerms')}
              </Button>
              <MyLojicLogo isDark={isDark} height={36} />
            </Box>
          </Container>
        </Box>

        <Container maxWidth="sm" sx={{ py: 4 }}>
          <Paper elevation={3} sx={{ p: { xs: 3, sm: 6 }, borderRadius: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, textAlign: 'center', mb: 0.5, color: colors.text.primary }}>
              {t('signup.title')}
            </Typography>
            <Typography sx={{ textAlign: 'center', mb: 4, color: colors.text.secondary }}>
              {t('signup.subtitle')}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3, bgcolor: colors.status.errorLight }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleEmailSignUp}>
              {/* Name Fields */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label={t('signup.firstNameLabel')}
                  placeholder={t('signup.firstNamePlaceholder')}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={isLoading}
                  required
                  autoFocus
                />
                <TextField
                  fullWidth
                  label={t('signup.lastNameLabel')}
                  placeholder={t('signup.lastNamePlaceholder')}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </Box>

              {/* Email Field — v3.79 PILOT-02: readOnly + pre-filled when invite code present */}
              <TextField
                fullWidth
                label={t('signup.emailLabel')}
                placeholder={t('signup.emailPlaceholder')}
                type="email"
                value={email}
                onChange={inviteCode ? undefined : (e) => setEmail(e.target.value)}
                disabled={isLoading}
                required
                autoComplete="email"
                sx={{ mb: 2 }}
                InputProps={inviteCode ? { readOnly: true } : undefined}
                helperText={inviteCode && inviteEmail ? `This invite is for ${inviteEmail}` : undefined}
              />

              {/* Password Field */}
              <TextField
                fullWidth
                label={t('signup.passwordLabel')}
                placeholder={t('signup.passwordPlaceholder')}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                required
                autoComplete="new-password"
                helperText={t('signup.passwordRequirements')}
                sx={{ mb: 3 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        disabled={isLoading}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Create Account Button */}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={isLoading || !firstName || !lastName || !email || !password}
                sx={{
                  bgcolor: colors.accent.main,
                  color: colors.accent.contrastText,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': { bgcolor: colors.accent.dark },
                  '&:disabled': { bgcolor: colors.border.main },
                }}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} sx={{ color: 'inherit', mr: 1 }} />
                    {t('signup.creatingAccount')}
                  </>
                ) : (
                  t('signup.submitButton')
                )}
              </Button>
            </Box>

            {/* v4.34 - WEBSITE-DISABLE-01 - temporarily disabled by Branko 2026-10-28: "Or sign up with" divider + Google / Microsoft buttons removed (row 785). */}

            {/* Sign In Link */}
            <Typography variant="body2" sx={{ textAlign: 'center', mt: 4, color: colors.text.secondary }}>
              {t('signup.hasAccount')}{' '}
              <Link
                component={RouterLink}
                to="/login"
                sx={{
                  color: colors.primary.main,
                  fontWeight: 600,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {t('signup.signIn')}
              </Link>
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  // PHASE 1: Consent (default)
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider', py: 2 }}>
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/')}
              sx={{ color: 'text.secondary' }}
            >
              Back
            </Button>
            <MyLojicLogo isDark={isDark} height={36} />
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, textAlign: 'center', color: 'secondary.main' }}>
          Terms and Conditions
        </Typography>
        <Typography sx={{ mb: 4, textAlign: 'center', color: 'text.secondary' }}>
          Please review and accept our End User License Agreement and Privacy Policy to continue
        </Typography>

        {/* Tabs */}
        <Paper elevation={2} sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {eulaAccepted && <CheckCircleIcon color="success" fontSize="small" />}
                  <span>EULA</span>
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {privacyAccepted && <CheckCircleIcon color="success" fontSize="small" />}
                  <span>Privacy Policy</span>
                </Box>
              }
            />
          </Tabs>

          {/* EULA Tab */}
          <Box
            role="tabpanel"
            hidden={activeTab !== 0}
          >
            {activeTab === 0 && (
              <>
                <Box
                  ref={eulaRef}
                  onScroll={(e) => handleScroll(e, 'eula')}
                  sx={{
                    maxHeight: 400,
                    overflow: 'auto',
                    bgcolor: 'grey.50',
                    border: 1,
                    borderColor: 'divider',
                    m: 2,
                    borderRadius: 1,
                  }}
                >
                  <EulaContent />
                </Box>

                {!eulaScrolled && (
                  <Alert severity="info" sx={{ mx: 2, mb: 2 }}>
                    Please scroll to the bottom to read the entire agreement
                  </Alert>
                )}

                <Box sx={{ px: 2, pb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={eulaAccepted}
                        onChange={(e) => setEulaAccepted(e.target.checked)}
                        disabled={!eulaScrolled}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '0.95rem' }}>
                        I have read and agree to the <strong>End User License Agreement</strong>
                      </Typography>
                    }
                  />
                </Box>
              </>
            )}
          </Box>

          {/* Privacy Tab */}
          <Box
            role="tabpanel"
            hidden={activeTab !== 1}
          >
            {activeTab === 1 && (
              <>
                <Box
                  ref={privacyRef}
                  onScroll={(e) => handleScroll(e, 'privacy')}
                  sx={{
                    maxHeight: 400,
                    overflow: 'auto',
                    bgcolor: 'grey.50',
                    border: 1,
                    borderColor: 'divider',
                    m: 2,
                    borderRadius: 1,
                  }}
                >
                  <PrivacyContent />
                </Box>

                {!privacyScrolled && (
                  <Alert severity="info" sx={{ mx: 2, mb: 2 }}>
                    Please scroll to the bottom to read the entire policy
                  </Alert>
                )}

                <Box sx={{ px: 2, pb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={privacyAccepted}
                        onChange={(e) => setPrivacyAccepted(e.target.checked)}
                        disabled={!privacyScrolled}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '0.95rem' }}>
                        I have read and agree to the <strong>Privacy Policy</strong>
                      </Typography>
                    }
                  />
                </Box>
              </>
            )}
          </Box>
        </Paper>

        {/* Status Summary */}
        <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'background.lightBlue' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Acceptance Status:
          </Typography>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon color={eulaAccepted ? 'success' : 'disabled'} fontSize="small" />
              <Typography sx={{ color: eulaAccepted ? 'success.main' : 'text.secondary' }}>
                EULA {eulaAccepted ? 'Accepted' : 'Not Accepted'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon color={privacyAccepted ? 'success' : 'disabled'} fontSize="small" />
              <Typography sx={{ color: privacyAccepted ? 'success.main' : 'text.secondary' }}>
                Privacy Policy {privacyAccepted ? 'Accepted' : 'Not Accepted'}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Continue Button */}
        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleContinueToSignup}
            disabled={!canContinue}
            sx={{
              bgcolor: 'accent.main',
              color: 'accent.contrastText',
              px: 6,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              '&:hover': {
                bgcolor: 'accent.dark',
              },
              '&:disabled': {
                bgcolor: 'grey.300',
              },
            }}
          >
            Continue to Registration
          </Button>
          {!canContinue && (
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              Please accept both agreements to continue
            </Typography>
          )}

          {/* Already have an account link */}
          <Typography variant="body2" sx={{ mt: 3, color: 'text.secondary' }}>
            Already have an account?{' '}
            <Link
              component={RouterLink}
              to="/login"
              sx={{
                color: colors.primary.main,
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Sign in
            </Link>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Consent;
